import {
    useDestroyFormStructureMutation,
    useExportFormStructuresMutation,
    useGetFormStructuresQuery,
    useUpdateFormStructureMutation,
} from "api/TenantSlug/FormStructure/hooks.ts";
import { FormStructureDrawer } from "components/Drawers/FormStructureDrawer/FormStructureDrawer";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { CountryCell } from "components/UI/Table/UI/CountryCell/CountryCell.tsx";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { LabelsCell } from "components/UI/Table/UI/LabelsCell/LabelsCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { FormStructureType, PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models.ts";

import FormStructureData = App.Data.FormStructureData;
import GetFormStructuresResponse = App.Http.Responses.FormStructure.GetFormStructuresResponse;
import GetFormStructureResponse = App.Http.Responses.FormStructure.GetFormStructureResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

interface FormStructureTableProps {
    type: FormStructureType;
}

export const FORM_STRUCTURE_SYSTEM_NAME_REGISTRATION_ADVERTISER = "registration-advertiser";
export const FORM_STRUCTURE_SYSTEM_NAME_REGISTRATION_AFFILIATE_PARTNER = "registration-affiliate-partner";

export const FormStructureTable = (props: FormStructureTableProps) => {
    const tableId = TableId.FormStructureTable;
    const tableAtomInstance = tableAtom<FormStructureData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const destroyFormStructureMutation = useDestroyFormStructureMutation();
    const updateFormStructureMutation = useUpdateFormStructureMutation(tableState.editingRowId || "");
    const exportState = useRecoilValue(exportAtom(tableId));
    const getStructuresExportMutation = useExportFormStructuresMutation();
    const { t: tG } = usePaldockTranslation("tables.general-columns");

    const request = {
        filter: {
            filter: {
                type: props.type,
                ...(tableState.filter?.filter || {}),
            },
            sort: tableState?.filter?.sort ?? "name",
            include: "country,category,labels",
        },
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
    };

    const getFormStructuresQuery = useGetFormStructuresQuery(request, { enabled: false });

    const tableFetchDataUtils = useTableDataApiOperations<
        GetFormStructuresResponse,
        GetFormStructureResponse,
        FormStructureData,
        BaseExportRequest
    >(
        tableId,
        getFormStructuresQuery,
        destroyFormStructureMutation,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TODO - to refactor
        updateFormStructureMutation,
        getStructuresExportMutation
    );

    const columnConfig: TableColumnType<FormStructureData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "name",
            key: "name",
            inputType: "text",
            sorter: true,
            defaultSorter: true,
            ellipsis: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        /*{
            title: <ColumnHeaderTitle columnTitle={t("columns.date")} />,
            dataIndex: "date",
            key: "date",
            inputType: "date",
        },*/
        {
            title: <ColumnHeaderTitle columnTitle={tG("system_name")} />,
            dataIndex: "system_name",
            key: "system_name",
            inputType: "text",
            sorter: true,
            ellipsis: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("country")} />,
            dataIndex: "country_id",
            key: "country_id",
            inputType: "text",
            render: (_, data: FormStructureData) => <CountryCell country={data.country} />,
            sorter: true,
            width: 100,
            filterable: true,
            filterType: FILTER_TYPES.countrySelect,
            filterName: "country_id",
            sortName: "country.name",
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("category")} />,
            dataIndex: "category.name",
            key: "category.name",
            render: (_, data: FormStructureData) => data.category?.name ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.categorySelect,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("tags")} />,
            dataIndex: "labels.name",
            key: "labels.name",
            render: (_, data) => <LabelsCell labels={data.labels} />,
            filterable: true,
            filterType: FILTER_TYPES.labelMultiSelect,
            ellipsis: false,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("id")} />,
            dataIndex: "id",
            key: "id",
            inputType: "text",
            render: (id) => <ShortenedIdWithCopy id={id} />,
            sorter: true,
            ellipsis: true,
            width: 160,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
    ];

    // Generate dynamic editUrl based on the type prop
    const getEditUrlForType = (type: FormStructureType): string => {
        switch (type) {
            case FormStructureType.User:
                return "/$tenantSlug/tool/structure/user/:id";
            case FormStructureType.Lead:
                return "/$tenantSlug/tool/structure/lead/:id";
            default:
                return "/$tenantSlug/tool/structure";
        }
    };

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<FormStructureData>({
        entityName: "formStructure",
        editPermission: PermissionEnum.FormStructureUpdate,
        detailPermission: PermissionEnum.FormStructureView,
        editUrl: getEditUrlForType(props.type),
    });

    return (
        <>
            <Table<GetFormStructuresResponse, FormStructureData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetFormStructuresResponse}
                dataSource={tableFetchDataUtils.tableData?.formStructures || []}
                columnConfig={columnConfig}
                loading={getFormStructuresQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                defaultDelete={tableFetchDataUtils.deleteTableData}
                defaultOnUpdate={tableFetchDataUtils.editTableData}
                addEntityData={{
                    drawerId: DrawerId.FormStructure,
                    permission: PermissionEnum.FormStructureCreate,
                }}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                enableFilters={true}
                searchDataIndex="name"
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                hideActionsColumn={true}
                autoScrollWidth={true}
            />
            <FormStructureDrawer type={props.type} onSubmit={() => tableFetchDataUtils.refetchData()} />
        </>
    );
};
